import type { CaseMoney, InvoiceSummary, ReceivableTranche } from '@/types/domain'

/* What is left of the agreed rate — the four figures the case's Invoices
 * section is built on.
 *
 * ── Why this is derived HERE and not asked for ─────────────────────────────
 *
 * Nothing new is fetched. Every figure below is already on the wire twice over:
 * `CaseMoney.rate` is the approved rate the case was registered at, and every
 * `InvoiceSummary` carries `approvedRateUsd`, `billedUsd` and `allocatedUsd` —
 * the AD-10 ceiling as the server itself judges it. What this file does is put
 * them in one shape so the screen states the same arithmetic the guard uses,
 * rather than a second opinion assembled inline in a component.
 *
 * ── Everything here is USD, and that is a statement rather than a shortcut ──
 *
 * The ceiling is booked in USD (AD-9), allocations settle in USD (AD-11), and
 * `billedUsd` sums the case in USD. `money.rate` is the same approved rate with
 * its own currency beside it, so it is read only when that currency IS USD; a
 * case priced in something else falls back to the invoices' own booked
 * `approvedRateUsd`, and answers `undefined` when there is neither. Adding two
 * units together to fill a headline figure is how a screen ends up reporting a
 * headroom nobody can spend.
 *
 * ── `billed` counts DRAFTS, and the caption has to say so ───────────────────
 *
 * `BilledOnCase` is Σ every invoice LINE on the case at booked USD, across
 * every invoice, drafts included (`finance/invoice_repo.go`). That is the
 * figure the guard refuses a charge against, so it is the figure the reader has
 * to be shown — a "billed" that quietly counted only issued paper would report
 * headroom the next line add would be refused for. The design mock shows the
 * issued-only reading; the server is the one that gets to be right, and the
 * section's caption names what is inside the number.
 *
 * Money crosses as decimal STRINGS in both directions (AD-9). The arithmetic
 * below is the display licence `invoice.ts` takes for `rateCeiling` and is
 * taken the same way: exact cents through BigInt, one rounding step, never a
 * float — a headroom that disagreed with a refusal by a cent would send
 * somebody to correct a charge that was right.
 */

export interface RateLedger {
  /** The unit every figure below is stated in. Always `USD` today; carried
   *  rather than assumed so a caller prints it instead of hard-coding it. */
  currency: string
  /** The agreed rate — the ceiling AD-10 judges this case's invoices against. */
  approved: string
  /** Σ every invoice line on the case at booked USD, DRAFTS INCLUDED. */
  billed: string
  /** Σ what has actually been allocated against those invoices. */
  paid: string
  /** `approved − billed`. NEGATIVE when the case is already past its ceiling,
   *  which is stated rather than clamped: a case over its rate is exactly the
   *  case somebody needs to see. */
  headroom: string
  /** Widths for the bar, 0..100. Decoration over the figures printed beside
   *  them — colour never carries meaning alone here — so rounding costs
   *  nothing, and an approved rate of zero would otherwise divide by it. */
  paidShare: number
  /** Billed but not yet paid, as a share of the rate. */
  awaitingShare: number
}

/** The four figures, or `undefined` when this case has no ceiling to state.
 *
 *  `undefined` is a real answer and the section renders a sentence for it: a
 *  case registered directly, with no quote behind it, has no agreed rate, no
 *  lines to prefill and no ceiling — and an invoice may still be raised on it.
 *  A ledger of zeros would report every such case as fully billed. */
export function rateLedger(
  money: CaseMoney | undefined,
  invoices: InvoiceSummary[],
): RateLedger | undefined {
  if (money === undefined) return undefined
  // `=== false` rather than falsy, exactly as the money section reads it: the
  // field is optional and an older payload that omits it is not a case that
  // was never priced.
  if (money.hasRate === false) return undefined

  const approved = approvedUsd(money, invoices)
  if (approved === undefined) return undefined

  /* Any invoice will do: `billedUsd` is a CASE-wide figure served identically
     on every row, not this invoice's share of it. Zero when there are none —
     which is the true billed total of a case nobody has invoiced yet, not a
     missing reading. */
  const billed = toCents(invoices[0]?.billedUsd ?? '0.00')
  const paid = invoices.reduce<bigint | undefined>(
    (sum, invoice) => add(sum, toCents(invoice.allocatedUsd)),
    0n,
  )
  if (billed === undefined || paid === undefined) return undefined

  const headroom = approved - billed
  // The unpaid half of what has been billed. Floored at zero rather than
  // allowed to go negative: over-allocation is refused server-side (AD-11), and
  // a negative width would paint the bar backwards if it ever arrived anyway.
  const awaiting = billed - paid > 0n ? billed - paid : 0n

  return {
    currency: 'USD',
    approved: fromCents(approved),
    billed: fromCents(billed),
    paid: fromCents(paid),
    headroom: fromCents(headroom),
    paidShare: share(paid, approved),
    awaitingShare: share(awaiting, approved),
  }
}

/** The approved rate in USD, from the case's own figure where it can be read in
 *  USD and from the invoices' booked ceiling otherwise. */
function approvedUsd(money: CaseMoney, invoices: InvoiceSummary[]): bigint | undefined {
  if (money.rate.currency === 'USD') return toCents(money.rate.amount)
  const booked = invoices.find((invoice) => invoice.approvedRateUsd !== null)
  return booked === undefined ? undefined : toCents(booked.approvedRateUsd as string)
}

function add(left: bigint | undefined, right: bigint | undefined): bigint | undefined {
  return left === undefined || right === undefined ? undefined : left + right
}

function share(part: bigint, whole: bigint): number {
  if (whole <= 0n) return 0
  const percent = (Number(part) / Number(whole)) * 100
  return Math.max(0, Math.min(100, percent))
}

/** A decimal string as whole cents, rounding half away from zero — the same
 *  rounding shopspring's `Round` does on the server side. `undefined` for
 *  anything that is not a number, which is the caller's cue to state nothing
 *  rather than to state a zero. */
function toCents(value: string): bigint | undefined {
  const text = value.replace(/\s/g, '').replace(',', '.').trim()
  if (!/^-?\d+(\.\d+)?$/.test(text)) return undefined

  const [whole = '', fraction = ''] = text.split('.')
  const negative = whole.startsWith('-')
  const digits = whole.replace('-', '') + fraction
  const units = BigInt(digits === '' ? '0' : digits)

  if (fraction.length <= 2) {
    const scaled = units * 10n ** BigInt(2 - fraction.length)
    return negative ? -scaled : scaled
  }
  const divisor = 10n ** BigInt(fraction.length - 2)
  const quotient = units / divisor
  const rounded = (units % divisor) * 2n >= divisor ? quotient + 1n : quotient
  return negative ? -rounded : rounded
}

/** Cents back to the two-place decimal string every money field on the wire is
 *  spelled as, sign included. */
function fromCents(cents: bigint): string {
  const negative = cents < 0n
  const magnitude = negative ? -cents : cents
  const whole = magnitude / 100n
  const fraction = String(magnitude % 100n).padStart(2, '0')
  return `${negative ? '-' : ''}${whole}.${fraction}`
}

/** What an instalment was actually billed, against what it is worth.
 *
 *  ── The failure this exists to name ─────────────────────────────────────────
 *
 *  An ISSUED invoice removes its tranche from the allocation registry outright,
 *  whatever it is for: the server's predicate is "does an issued invoice name
 *  this tranche", not "does one cover it" (see `tranches_target.go`). So an
 *  instalment worth 2 000.00 billed by a 500.00 invoice stops being expected at
 *  all, and the remaining 1 500.00 leaves the case's receivable without anybody
 *  deciding that it should. The schedule printed the tranche's own figure beside
 *  that invoice's number, which reads as "2 000.00, billed" — the one reading
 *  that is certainly wrong.
 *
 *  Only ISSUED invoices count here, because only they displace. A draft against
 *  a tranche is somebody's work in progress and the tranche is still owed.
 *
 *  `undefined` when there is nothing to say: no issued paper, or figures that do
 *  not parse. A comparison this cannot make honestly is one it does not make —
 *  see `toCents`.
 *
 *  Currency is compared BEFORE the figures. A tranche in USD billed by a CNY
 *  invoice is not 500 short of 2 000; it is two amounts that cannot be
 *  subtracted, and inventing a rate here would be this file restating a booking
 *  the server froze (AD-9). Such a pair reports `comparable: false`, and the
 *  screen says the invoice exists without claiming anything about the gap.
 */
export interface TrancheBilling {
  /** Σ the issued invoices naming this tranche, as a decimal string. */
  billed: string
  /** Whether that sum and the tranche are in one currency. */
  comparable: boolean
  /** True when the two are in one currency and disagree. */
  short: boolean
}

export function trancheBilling(
  tranche: ReceivableTranche,
  invoices: InvoiceSummary[],
): TrancheBilling | undefined {
  const issued = invoices.filter(
    (invoice) => invoice.trancheId === tranche.id && invoice.status === 'issued',
  )
  if (issued.length === 0) return undefined

  const comparable = issued.every(
    (invoice) => invoice.currency === tranche.amount.currency,
  )

  let total: bigint | undefined = 0n
  for (const invoice of issued) total = add(total, toCents(invoice.totalAmount))
  if (total === undefined) return undefined

  const expected = toCents(tranche.amount.amount)
  return {
    billed: fromCents(total),
    comparable,
    short: comparable && expected !== undefined && total !== expected,
  }
}
